import { useEffect, useState } from "react";
import { buildCompleteAtOverride } from "@src/common/utils/timezoneConverters";
import {
  BaseModal,
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
  Checkbox,
  Loading,
  Tooltip,
} from "@src/common/components";
import {
  useForceCompleteShipmentMutation,
  useForceCompleteStopsMutation,
  Stop,
} from "@api/graphql/generated/generated-types";
import { useCourieStore } from "@src/common/lib/store";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import StopCard from "@src/common/components/StopCard/StopCard";
import CourieButton from "@src/common/components/Button/Button";
import { compareStopsForDisplay } from "@src/shipments/utils/stopOrdering";
import { CheckIcon } from "@heroicons/react/24/outline";
import { CompletionOverrideDateTimeFields } from "../CompletionOverrideDateTimeFields";
import {
  ForceCompleteModalShipment,
  useForceCompleteModalShipment,
} from "./hooks/useForceCompleteModalShipment";
import { useAuth } from "@src/common/hooks/useAuth";

interface ForceCompleteModalProps {
  isModalOpen: boolean;
  shipmentId: string;
  onClose: () => void;
  onSuccess: () => void;
  onOpenOverrideCompletionTimes?: (shipmentId: string) => void;
}

interface ForceCompleteModalContentProps extends ForceCompleteModalProps {
  shipment: ForceCompleteModalShipment;
}

const ForceCompleteModalContent = ({
  isModalOpen,
  shipmentId,
  onClose,
  onSuccess,
  onOpenOverrideCompletionTimes,
  shipment,
}: ForceCompleteModalContentProps) => {
  const [selectedStopIds, setSelectedStopIds] = useState<string[]>([]);
  const { courieUser, courier, employeeId } = useAuth();
  const { showToast } = useCourieStore();
  const courierId = courier?.courier?.id;
  const stops = shipment?.stops;
  const uncompletedStops = stops?.filter((stop) => !stop.completedAt) || [];
  const [
    forceCompleteShipment,
    {
      loading: forceCompleteShipmentLoading,
      error: forceCompleteShipmentError,
    },
  ] = useForceCompleteShipmentMutation();

  const [
    forceCompleteStops,
    { loading: forceCompleteStopsLoading, error: forceCompleteStopsError },
  ] = useForceCompleteStopsMutation();

  const loading = forceCompleteShipmentLoading || forceCompleteStopsLoading;

  const [dateCompletedOverride, setDateCompletedOverride] =
    useState<Date | null>(null);
  const [completionTimeOverride, setCompletionTimeOverride] =
    useState<Date | null>(null);
  const [shouldOverrideCompletionTimes, setShouldOverrideCompletionTimes] =
    useState(true);

  useEffect(() => {
    if (forceCompleteShipmentError) {
      showErrorToast(forceCompleteShipmentError, showToast);
    }
    if (forceCompleteStopsError) {
      showErrorToast(forceCompleteStopsError, showToast);
    }
  }, [forceCompleteShipmentError, forceCompleteStopsError, showToast]);

  useEffect(() => {
    // Select all stops by default when modal opens
    if (isModalOpen && stops) {
      setSelectedStopIds(
        stops.filter((stop) => !stop.completedAt).map((stop) => stop.id)
      );
    }
  }, [isModalOpen, stops]);

  const handleToggleStop = (stopId: string) => {
    setDateCompletedOverride(null);
    setCompletionTimeOverride(null);
    setSelectedStopIds((prev) =>
      prev.includes(stopId)
        ? prev.filter((id) => id !== stopId)
        : [...prev, stopId]
    );
  };

  const handleConfirm = async () => {
    if (!stops || !courierId) return;

    try {
      if (selectedStopIds.length === uncompletedStops.length) {
        await forceCompleteShipment({
          variables: {
            courierId: courierId,
            shipmentId: shipmentId,
            completedBy: employeeId ? employeeId : courieUser!.id,
            completeAtOverride: buildCompleteAtOverride(
              dateCompletedOverride,
              completionTimeOverride
            ),
          },
        });
        showToast({
          message: "The order is force completed successfully",
          type: "success",
        });
      } else {
        await forceCompleteStops({
          variables: {
            courierId: courierId,
            shipmentId,
            stopIds: selectedStopIds,
            completedBy: employeeId ? employeeId : courieUser!.id,
          },
        });
        showToast({
          message: "The selected stops were force completed successfully",
          type: "success",
        });
      }
      onClose();
      if (shouldOverrideCompletionTimes && onOpenOverrideCompletionTimes) {
        onOpenOverrideCompletionTimes(shipmentId);
      } else {
        onSuccess();
      }
    } catch (error) {
      // Errors are handled by the useEffect above
    }
  };

  if (!stops) return null;

  const isCompletionOverridePartiallyFilled =
    !!dateCompletedOverride !== !!completionTimeOverride;

  return (
    <BaseModal
      isOpen={isModalOpen}
      onClose={onClose}
      closeable
      size="default"
      autoFocus={false}
    >
      <BaseModalHeader>Force Complete Stops</BaseModalHeader>
      <BaseModalBody>
        <CompletionOverrideDateTimeFields
          date={dateCompletedOverride}
          time={completionTimeOverride}
          onDateChange={setDateCompletedOverride}
          onTimeChange={setCompletionTimeOverride}
          disabled={selectedStopIds.length !== uncompletedStops.length}
        />

        <p className="mb-4">Select the stops you want to force complete:</p>
        <div className="space-y-2">
          {[...stops]
            .sort(compareStopsForDisplay)
            .map((stop, i) => (
              <div key={stop.id} className="flex items-center">
                {stop.completedAt ? (
                  <Tooltip content="Completed">
                    <div className="w-4 h-4 bg-green-300 rounded-full">
                      <CheckIcon className="w-4 h-4 text-white" />
                    </div>
                  </Tooltip>
                ) : (
                  <Checkbox
                    checked={selectedStopIds.includes(stop.id)}
                    onChange={() => handleToggleStop(stop.id)}
                  />
                )}
                <StopCard stop={stop as Stop} />
              </div>
            ))}
        </div>
        <div className="h-8 mt-2 mb-4">
          {selectedStopIds.length === uncompletedStops.length && (
            <p className="text-sm text-gray-500 text-center">
              Are you sure you want to complete this order on behalf of the
              driver without POD?
            </p>
          )}
        </div>
      </BaseModalBody>
      <BaseModalFooter>
        <div className="flex justify-between items-center border-t border-gray-200 pt-4">
          <CourieButton onClick={onClose} color="secondary">
            Cancel
          </CourieButton>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={shouldOverrideCompletionTimes}
                onChange={() =>
                  setShouldOverrideCompletionTimes((prev) => !prev)
                }
              />
              <span className="text-sm">Override completion times</span>
            </label>
            <CourieButton
              onClick={handleConfirm}
              disabled={
                selectedStopIds.length === 0 ||
                loading ||
                isCompletionOverridePartiallyFilled
              }
              isProcessing={loading}
              outline={false}
              color={"failure"}
            >
              Force Complete
            </CourieButton>
          </div>
        </div>
      </BaseModalFooter>
    </BaseModal>
  );
};

const ForceCompleteModal = ({
  isModalOpen,
  shipmentId,
  onClose,
  onSuccess,
  onOpenOverrideCompletionTimes,
}: ForceCompleteModalProps) => {
  const { courier } = useAuth();
  const courierId = courier?.courier?.id;

  const { shipment, loading, error } = useForceCompleteModalShipment({
    courierId,
    shipmentId,
    skip: !isModalOpen,
  });

  if (!isModalOpen) return null;

  if (error || (!loading && !shipment)) {
    return (
      <BaseModal
        isOpen={isModalOpen}
        onClose={onClose}
        closeable
        size="default"
        autoFocus={false}
      >
        <BaseModalHeader>Force Complete Stops</BaseModalHeader>
        <BaseModalBody>
          <div className="py-8 text-center text-sm text-gray-600 dark:text-gray-300">
            Could not load shipment. Try again or close this dialog.
          </div>
        </BaseModalBody>
      </BaseModal>
    );
  }

  if (loading || !shipment) {
    return (
      <BaseModal
        isOpen={isModalOpen}
        onClose={onClose}
        closeable
        size="default"
        autoFocus={false}
      >
        <BaseModalHeader>Force Complete Stops</BaseModalHeader>
        <BaseModalBody>
          <div className="flex justify-center py-12">
            <Loading loading />
          </div>
        </BaseModalBody>
      </BaseModal>
    );
  }

  return (
    <ForceCompleteModalContent
      isModalOpen={isModalOpen}
      shipmentId={shipmentId}
      onClose={onClose}
      onSuccess={onSuccess}
      onOpenOverrideCompletionTimes={onOpenOverrideCompletionTimes}
      shipment={shipment}
    />
  );
};

export default ForceCompleteModal;
